/**
 * ====================================================================
 * FIKO CAD ARROW FACTORY
 * ====================================================================
 *
 * Profesyonel CAD ok geometrileri fabrikası
 * CATIA, SolidWorks, AutoCAD tarzı oklar
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { ExtendedArrowStyle, ArrowStyleConfig } from '../visual/DimensionVisualStyle';

// ====================================================================
// ARROW FACTORY
// ====================================================================

/**
 * Arrow Factory - Profesyonel ok geometrileri
 */
export class ArrowFactory {
    // Geometry cache - performans için
    private static geometryCache = new Map<string, THREE.BufferGeometry>();

    // ⚡ PERFORMANS: Material pool - ayni renk+opacity icin tek material
    private static materialPool = new Map<string, THREE.MeshBasicMaterial>();

    // Pre-allocated vectors for zero-allocation operations
    private static readonly _tempVec3 = new THREE.Vector3();
    private static readonly _tempQuat = new THREE.Quaternion();
    private static readonly _tempMatrix = new THREE.Matrix4();
    private static readonly _tempDefaultDir = new THREE.Vector3(1, 0, 0);
    private static readonly _tempUp = new THREE.Vector3();
    private static readonly _tempDir = new THREE.Vector3();
    private static readonly _tempScale = new THREE.Vector3(1, 1, 1);

    /**
     * Cache'i temizle
     */
    static clearCache(): void {
        this.geometryCache.forEach(geometry => geometry.dispose());
        this.geometryCache.clear();
        this.materialPool.forEach(m => m.dispose());
        this.materialPool.clear();
    }

    /**
     * ⚡ PERFORMANS: Pooled material al - ayni renk+opacity icin tek instance
     */
    private static getPooledMaterial(color: number, opacity: number = 1): THREE.MeshBasicMaterial {
        const key = `${color}_${opacity}`;
        let mat = this.materialPool.get(key);
        if (!mat) {
            mat = new THREE.MeshBasicMaterial({
                color,
                transparent: opacity < 1,
                opacity,
                side: THREE.DoubleSide,
                depthTest: false,
                depthWrite: false
            });
            this.materialPool.set(key, mat);
        }
        return mat;
    }

    /**
     * Cache boyutunu al
     */
    static getCacheSize(): number {
        return this.geometryCache.size;
    }

    /**
     * Ana fabrika metodu - Ok geometrisi oluştur
     */
    static createArrow(
        style: ExtendedArrowStyle,
        config: ArrowStyleConfig
    ): THREE.BufferGeometry {
        const cacheKey = this.getCacheKey(style, config);

        // ⚡ Cache hit - geometry paylasilir, clone yok (Mesh zaten kendi transform'unu tutar)
        if (this.geometryCache.has(cacheKey)) {
            return this.geometryCache.get(cacheKey)!;
        }

        let geometry: THREE.BufferGeometry;

        switch (style) {
            case ExtendedArrowStyle.FILLED:
                geometry = this.createFilledArrow(config);
                break;
            case ExtendedArrowStyle.OPEN:
                geometry = this.createOpenArrow(config);
                break;
            case ExtendedArrowStyle.DOT:
                geometry = this.createDotArrow(config);
                break;
            case ExtendedArrowStyle.TICK:
                geometry = this.createTickArrow(config);
                break;
            case ExtendedArrowStyle.ARCH:
                geometry = this.createArchArrow(config);
                break;
            case ExtendedArrowStyle.BOX:
                geometry = this.createBoxArrow(config);
                break;
            case ExtendedArrowStyle.OBLIQUE:
                geometry = this.createObliqueArrow(config);
                break;
            case ExtendedArrowStyle.SLASH:
                geometry = this.createSlashArrow(config);
                break;
            case ExtendedArrowStyle.DOUBLE:
                geometry = this.createDoubleArrow(config);
                break;
            case ExtendedArrowStyle.INTEGRAL:
                geometry = this.createIntegralArrow(config);
                break;
            case ExtendedArrowStyle.DATUM:
                geometry = this.createDatumArrow(config);
                break;
            case ExtendedArrowStyle.ORIGIN:
                geometry = this.createOriginArrow(config);
                break;
            case ExtendedArrowStyle.NONE:
            default:
                geometry = new THREE.BufferGeometry();
                break;
        }

        // Cache'e ekle
        this.geometryCache.set(cacheKey, geometry);

        // ⚡ Clone yok - geometry paylasilir
        return geometry;
    }

    /**
     * Cache key oluştur
     */
    private static getCacheKey(style: ExtendedArrowStyle, config: ArrowStyleConfig): string {
        return `${style}_${config.size.toFixed(3)}_${config.aspectRatio.toFixed(2)}_${config.hollowThickness || 0}_${config.curvature || 0}_${config.angle || 0}`;
    }

    // ====================================================================
    // ARROW TYPES
    // ====================================================================

    /**
     * FILLED - Dolu üçgen ok (CAD standardı)
     */
    private static createFilledArrow(config: ArrowStyleConfig): THREE.BufferGeometry {
        const { size, aspectRatio } = config;
        const length = size;
        const width = size / aspectRatio;

        const shape = new THREE.Shape();

        // Üçgen ok - ucu +X yönünde
        shape.moveTo(length, 0);           // Uç
        shape.lineTo(-length * 0.2, width / 2);    // Sol üst
        shape.lineTo(-length * 0.1, 0);    // Orta giriş
        shape.lineTo(-length * 0.2, -width / 2);   // Sol alt
        shape.closePath();

        const geometry = new THREE.ShapeGeometry(shape);

        // XY düzleminde, X+ yönünde ok
        return geometry;
    }

    /**
     * OPEN - Açık V şeklinde ok
     */
    private static createOpenArrow(config: ArrowStyleConfig): THREE.BufferGeometry {
        const { size, aspectRatio, hollowThickness = 0.15 } = config;
        const length = size;
        const width = size / aspectRatio;
        const thickness = hollowThickness * size;

        const shape = new THREE.Shape();

        // Dış V
        shape.moveTo(length, 0);
        shape.lineTo(-length * 0.3, width / 2);
        shape.lineTo(-length * 0.3 + thickness, width / 2 - thickness * 0.5);
        shape.lineTo(length - thickness * 2, 0);
        shape.lineTo(-length * 0.3 + thickness, -width / 2 + thickness * 0.5);
        shape.lineTo(-length * 0.3, -width / 2);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * DOT - Daire ok
     */
    private static createDotArrow(config: ArrowStyleConfig): THREE.BufferGeometry {
        const radius = config.size * 0.3;
        const segments = 24;

        const geometry = new THREE.CircleGeometry(radius, segments);
        return geometry;
    }

    /**
     * TICK - Çizgi ok (architectural)
     */
    private static createTickArrow(config: ArrowStyleConfig): THREE.BufferGeometry {
        const { size, angle = 45 } = config;
        const length = size * 0.8;
        const angleRad = (angle * Math.PI) / 180;

        // Çizgi olarak (kalın çizgi için plane kullan)
        const thickness = size * 0.08;

        const shape = new THREE.Shape();

        // 45 derece eğik çizgi
        const dx = Math.cos(angleRad) * length;
        const dy = Math.sin(angleRad) * length;

        // Kalın çizgi olarak dikdörtgen
        const perpX = -Math.sin(angleRad) * thickness;
        const perpY = Math.cos(angleRad) * thickness;

        shape.moveTo(-dx / 2 + perpX, -dy / 2 + perpY);
        shape.lineTo(dx / 2 + perpX, dy / 2 + perpY);
        shape.lineTo(dx / 2 - perpX, dy / 2 - perpY);
        shape.lineTo(-dx / 2 - perpX, -dy / 2 - perpY);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * ARCH - Yay şeklinde ok (CATIA tarzı)
     */
    private static createArchArrow(config: ArrowStyleConfig): THREE.BufferGeometry {
        const { size, aspectRatio, curvature = 0.4 } = config;
        const length = size;
        const width = size / aspectRatio;

        const shape = new THREE.Shape();

        // Quadratic bezier ile yay çiz
        shape.moveTo(length, 0);

        // Üst kavis
        shape.quadraticCurveTo(
            length * curvature,           // Control point X
            width / 2,                    // Control point Y
            -length * 0.2,               // End X
            width / 2                    // End Y
        );

        // Alt kenar (düz veya hafif kavisli)
        shape.lineTo(-length * 0.1, 0);

        // Alt kavis
        shape.lineTo(-length * 0.2, -width / 2);

        shape.quadraticCurveTo(
            length * curvature,          // Control point X
            -width / 2,                  // Control point Y
            length,                      // End X
            0                           // End Y
        );

        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * BOX - Kare ok
     */
    private static createBoxArrow(config: ArrowStyleConfig): THREE.BufferGeometry {
        const boxSize = config.size * 0.4;

        const geometry = new THREE.PlaneGeometry(boxSize, boxSize);
        return geometry;
    }

    /**
     * OBLIQUE - Eğik ok (30°)
     */
    private static createObliqueArrow(config: ArrowStyleConfig): THREE.BufferGeometry {
        const { size, angle = 30 } = config;
        const length = size;
        const width = size * 0.4;
        const angleRad = (angle * Math.PI) / 180;

        const shape = new THREE.Shape();

        // Eğik ok
        shape.moveTo(length, 0);
        shape.lineTo(0, width / 2);

        // Eğik kenar (kayma)
        const shearX = Math.tan(angleRad) * width;
        shape.lineTo(-shearX, width / 2);
        shape.lineTo(length - shearX * 2, 0);
        shape.lineTo(-shearX, -width / 2);
        shape.lineTo(0, -width / 2);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * SLASH - / şeklinde ok (architectural)
     */
    private static createSlashArrow(config: ArrowStyleConfig): THREE.BufferGeometry {
        const { size } = config;
        const length = size * 0.8;
        const thickness = size * 0.1;

        const shape = new THREE.Shape();

        // 45 derece slash
        const cos45 = Math.SQRT1_2;
        const sin45 = Math.SQRT1_2;

        const halfLen = length / 2;
        const halfThick = thickness / 2;

        // Dikdörtgen, 45 derece döndürülmüş
        shape.moveTo(-halfLen * cos45 - halfThick * sin45, -halfLen * sin45 + halfThick * cos45);
        shape.lineTo(halfLen * cos45 - halfThick * sin45, halfLen * sin45 + halfThick * cos45);
        shape.lineTo(halfLen * cos45 + halfThick * sin45, halfLen * sin45 - halfThick * cos45);
        shape.lineTo(-halfLen * cos45 + halfThick * sin45, -halfLen * sin45 - halfThick * cos45);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * DOUBLE - >> çift ok
     */
    private static createDoubleArrow(config: ArrowStyleConfig): THREE.BufferGeometry {
        const { size, aspectRatio } = config;
        const length = size * 0.6;
        const width = size / aspectRatio;
        const gap = size * 0.2;

        const shape = new THREE.Shape();

        // İlk ok
        shape.moveTo(length, 0);
        shape.lineTo(-length * 0.1, width / 2);
        shape.lineTo(0, 0);
        shape.lineTo(-length * 0.1, -width / 2);
        shape.closePath();

        // İkinci ok (offset)
        const hole = new THREE.Path();
        const offsetX = -gap;
        hole.moveTo(length + offsetX, 0);
        hole.lineTo(-length * 0.1 + offsetX, width / 2);
        hole.lineTo(0 + offsetX, 0);
        hole.lineTo(-length * 0.1 + offsetX, -width / 2);
        hole.closePath();

        // İki ayrı şekil birleştir (merge geometry)
        const shape1 = new THREE.Shape();
        shape1.moveTo(length, 0);
        shape1.lineTo(-length * 0.1, width / 2);
        shape1.lineTo(0, 0);
        shape1.lineTo(-length * 0.1, -width / 2);
        shape1.closePath();

        const shape2 = new THREE.Shape();
        shape2.moveTo(length + offsetX, 0);
        shape2.lineTo(-length * 0.1 + offsetX, width / 2);
        shape2.lineTo(0 + offsetX, 0);
        shape2.lineTo(-length * 0.1 + offsetX, -width / 2);
        shape2.closePath();

        const geometry1 = new THREE.ShapeGeometry(shape1);
        const geometry2 = new THREE.ShapeGeometry(shape2);

        // Merge geometries
        const mergedGeometry = this.mergeBufferGeometries([geometry1, geometry2]);

        geometry1.dispose();
        geometry2.dispose();

        return mergedGeometry;
    }

    /**
     * INTEGRAL - ∫ integral işareti
     */
    private static createIntegralArrow(config: ArrowStyleConfig): THREE.BufferGeometry {
        const { size } = config;
        const height = size;
        const width = size * 0.3;
        const thickness = size * 0.1;

        const shape = new THREE.Shape();

        // S şeklinde integral işareti
        const curvePoints: THREE.Vector2[] = [];

        // Üst kıvrım
        for (let t = 0; t <= 1; t += 0.1) {
            const angle = Math.PI * t;
            const x = width * 0.3 * Math.cos(angle);
            const y = height / 2 - width * 0.3 * Math.sin(angle) - width * 0.3;
            curvePoints.push(new THREE.Vector2(x, y));
        }

        // Düz çizgi
        curvePoints.push(new THREE.Vector2(-width * 0.3, -height / 2 + width * 0.5));

        // Alt kıvrım
        for (let t = 0; t <= 1; t += 0.1) {
            const angle = Math.PI + Math.PI * t;
            const x = -width * 0.3 + width * 0.3 * Math.cos(angle);
            const y = -height / 2 + width * 0.3 + width * 0.3 * Math.sin(angle);
            curvePoints.push(new THREE.Vector2(x, y));
        }

        // Basit dikdörtgen olarak
        shape.moveTo(-thickness / 2, height / 2);
        shape.lineTo(thickness / 2, height / 2);
        shape.lineTo(thickness / 2, -height / 2);
        shape.lineTo(-thickness / 2, -height / 2);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * DATUM - ▽ datum üçgen
     */
    private static createDatumArrow(config: ArrowStyleConfig): THREE.BufferGeometry {
        const { size } = config;
        const height = size;
        const width = size * 0.8;

        const shape = new THREE.Shape();

        // Ters üçgen (▽)
        shape.moveTo(0, height / 2);
        shape.lineTo(width / 2, -height / 2);
        shape.lineTo(-width / 2, -height / 2);
        shape.closePath();

        return new THREE.ShapeGeometry(shape);
    }

    /**
     * ORIGIN - ⊙ origin işareti (iç içe daireler)
     */
    private static createOriginArrow(config: ArrowStyleConfig): THREE.BufferGeometry {
        const { size } = config;
        const outerRadius = size * 0.4;
        const innerRadius = size * 0.15;
        const segments = 24;

        // Dış daire
        const outerShape = new THREE.Shape();
        for (let i = 0; i <= segments; i++) {
            const angle = (i / segments) * Math.PI * 2;
            const x = Math.cos(angle) * outerRadius;
            const y = Math.sin(angle) * outerRadius;
            if (i === 0) {
                outerShape.moveTo(x, y);
            } else {
                outerShape.lineTo(x, y);
            }
        }

        // İç daire (delik)
        const holePath = new THREE.Path();
        for (let i = 0; i <= segments; i++) {
            const angle = (i / segments) * Math.PI * 2;
            const x = Math.cos(angle) * innerRadius;
            const y = Math.sin(angle) * innerRadius;
            if (i === 0) {
                holePath.moveTo(x, y);
            } else {
                holePath.lineTo(x, y);
            }
        }

        outerShape.holes.push(holePath);

        // Merkez nokta için ayrı geometry
        const centerGeometry = new THREE.CircleGeometry(innerRadius * 0.5, 16);
        const ringGeometry = new THREE.ShapeGeometry(outerShape);

        // Birleştir
        const merged = this.mergeBufferGeometries([ringGeometry, centerGeometry]);

        ringGeometry.dispose();
        centerGeometry.dispose();

        return merged;
    }

    // ====================================================================
    // UTILITY METHODS
    // ====================================================================

    /**
     * Buffer geometrileri birleştir
     */
    private static mergeBufferGeometries(geometries: THREE.BufferGeometry[]): THREE.BufferGeometry {
        // Toplam vertex sayısını hesapla
        let totalVertices = 0;
        let totalIndices = 0;

        for (const geo of geometries) {
            const position = geo.getAttribute('position');
            if (position) {
                totalVertices += position.count;
            }
            const index = geo.getIndex();
            if (index) {
                totalIndices += index.count;
            }
        }

        // Yeni buffer'lar oluştur
        const positions = new Float32Array(totalVertices * 3);
        const uvs = new Float32Array(totalVertices * 2);
        const indices: number[] = [];

        let vertexOffset = 0;
        let indexOffset = 0;

        for (const geo of geometries) {
            const position = geo.getAttribute('position');
            const uv = geo.getAttribute('uv');
            const index = geo.getIndex();

            if (position) {
                for (let i = 0; i < position.count; i++) {
                    positions[(vertexOffset + i) * 3] = position.getX(i);
                    positions[(vertexOffset + i) * 3 + 1] = position.getY(i);
                    positions[(vertexOffset + i) * 3 + 2] = position.getZ(i);
                }
            }

            if (uv) {
                for (let i = 0; i < uv.count; i++) {
                    uvs[(vertexOffset + i) * 2] = uv.getX(i);
                    uvs[(vertexOffset + i) * 2 + 1] = uv.getY(i);
                }
            }

            if (index) {
                for (let i = 0; i < index.count; i++) {
                    indices.push(index.getX(i) + vertexOffset);
                }
            }

            if (position) {
                vertexOffset += position.count;
            }
        }

        // Yeni geometry oluştur
        const mergedGeometry = new THREE.BufferGeometry();
        mergedGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        mergedGeometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));

        if (indices.length > 0) {
            mergedGeometry.setIndex(indices);
        }

        mergedGeometry.computeVertexNormals();

        return mergedGeometry;
    }

    /**
     * Ok geometrisini pozisyon ve yöne göre dönüştür
     */
    static transformArrow(
        geometry: THREE.BufferGeometry,
        position: THREE.Vector3,
        direction: THREE.Vector3,
        up?: THREE.Vector3
    ): THREE.BufferGeometry {
        // ⚡ Clone gerekli - geometry modifiye ediliyor (applyMatrix4)
        const transformed = geometry.clone();

        // ⚡ Pre-allocated buffers kullan
        const quaternion = ArrowFactory._tempQuat;

        if (up) {
            ArrowFactory._tempMatrix.lookAt(ArrowFactory._tempVec3.set(0, 0, 0), direction, up);
            quaternion.setFromRotationMatrix(ArrowFactory._tempMatrix);
        } else {
            ArrowFactory._tempDefaultDir.set(1, 0, 0);
            ArrowFactory._tempDir.copy(direction).normalize();
            quaternion.setFromUnitVectors(ArrowFactory._tempDefaultDir, ArrowFactory._tempDir);
        }

        ArrowFactory._tempMatrix.compose(position, quaternion, ArrowFactory._tempScale.set(1, 1, 1));
        transformed.applyMatrix4(ArrowFactory._tempMatrix);

        return transformed;
    }

    /**
     * Ok mesh'i oluştur (material ile)
     */
    static createArrowMesh(
        style: ExtendedArrowStyle,
        config: ArrowStyleConfig,
        color: number = 0x000000,
        opacity: number = 1
    ): THREE.Mesh {
        const geometry = this.createArrow(style, config);
        // ⚡ PERFORMANS: Pooled material kullan
        const material = this.getPooledMaterial(color, opacity);
        return new THREE.Mesh(geometry, material);
    }

    /**
     * Ok çiftini oluştur (dimension line için iki uç)
     */
    static createArrowPair(
        style: ExtendedArrowStyle,
        config: ArrowStyleConfig,
        startPoint: THREE.Vector3,
        endPoint: THREE.Vector3,
        color: number = 0x000000
    ): { start: THREE.Mesh; end: THREE.Mesh } {
        // ⚡ _tempVec3 direction icin kullaniliyor, _tempDir negate icin
        const direction = this._tempVec3.subVectors(endPoint, startPoint).normalize();

        // Başlangıç oku (ters yön)
        const startArrow = this.createArrowMesh(style, config, color);
        startArrow.position.copy(startPoint);
        this._tempDefaultDir.set(1, 0, 0);
        this._tempDir.copy(direction).negate();
        startArrow.quaternion.setFromUnitVectors(this._tempDefaultDir, this._tempDir);

        // Bitiş oku
        const endArrow = this.createArrowMesh(style, config, color);
        endArrow.position.copy(endPoint);
        this._tempDefaultDir.set(1, 0, 0);
        endArrow.quaternion.setFromUnitVectors(this._tempDefaultDir, direction);

        return { start: startArrow, end: endArrow };
    }
}

// ====================================================================
// EXPORTS
// ====================================================================

export { ExtendedArrowStyle };
export type { ArrowStyleConfig };
